import { useState, useEffect } from "react";
import "./AdminDashboard.css";
import AdminProperties from "./AdminProperties";
import AdminCars from "./AdminCars";

const API = "/api";

const NAV = [
  { id: "dashboard", icon: "📊", label: "Dashboard" },
  { id: "properties", icon: "🏠", label: "Properties" },
  { id: "cars", icon: "🚗", label: "Cars" },
  { id: "destinations", icon: "📍", label: "Destinations" },
  { id: "beds24", icon: "📅", label: "Beds24 Live" },
  { id: "blog", icon: "✍️", label: "Blog" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [destForm, setDestForm] = useState({ id: null, city: "", tag: "", description: "", image: "" });
  const [destEditing, setDestEditing] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", slug: "", excerpt: "", content: "", image: "", category: "Travel", featured: false });
  const [blogEditing, setBlogEditing] = useState(null);
  const [blogView, setBlogView] = useState("list");
  const [msg, setMsg] = useState("");

  const token = localStorage.getItem("adminToken");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchUser();
    fetchProperties();
    fetchBookings();
    fetchBlogs();
    loadDestinations();
  }, []);

  async function fetchUser() {
    try {
      const res = await fetch(`${API}/auth/me`, { headers });
      const data = await res.json();
      setUser(data.user);
    } catch (e) {}
  }

  async function fetchProperties() {
    try {
      const res = await fetch(`${API}/properties/admin/all`, { headers });
      const data = await res.json();
      setProperties(Array.isArray(data) ? data : data.properties || []);
    } catch (e) {}
  }

  async function fetchBookings() {
    try {
      const res = await fetch(`${API}/beds24/bookings`, { headers });
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : data.bookings || []);
    } catch (e) { setBookings([]); }
  }

  async function fetchBlogs() {
    try {
      const res = await fetch(`${API}/blog`, { headers });
      const data = await res.json();
      setBlogs(Array.isArray(data) ? data : data.posts || []);
    } catch (e) { setBlogs([]); }
  }

  function loadDestinations() {
    const saved = localStorage.getItem("adminDestinations");
    if (saved) setDestinations(JSON.parse(saved));
    else setDestinations([
      { id: 1, city: "Tirana", tag: "Capital", description: "Colourful boulevards, cafe culture and modern energy", image: "./tirana.jpg" },
      { id: 2, city: "Pogradec", tag: "Lakeside", description: "Crystalline Lake Ohrid and mountain reflections", image: "./pogradec.jpg" },
      { id: 3, city: "Qerret", tag: "Coastal", description: "Adriatic sunsets and golden sand near Kavaje", image: "./qerret.jpg" },
    ]);
  }

  function saveDestinations(dests) {
    setDestinations(dests);
    localStorage.setItem("adminDestinations", JSON.stringify(dests));
  }

  function logout() {
    localStorage.removeItem("adminToken");
    window.location.href = "/admin";
  }

  const today = new Date().toISOString().split("T")[0];
  const safeBookings = Array.isArray(bookings) ? bookings : [];
  const safeProperties = Array.isArray(properties) ? properties : [];
  const todayArrivals = safeBookings.filter(b => b.firstNight === today && b.firstName !== "Block");
  const todayDepartures = safeBookings.filter(b => b.lastNight === today && b.firstName !== "Block");
  const activeProps = safeProperties.filter(p => p.active).length;
  const featuredProps = safeProperties.filter(p => p.featured).length;

  function DashboardTab() {
    return (
      <div>
        <div className="admin-section-title">Dashboard</div>
        <div className="admin-section-sub">Welcome back{user ? ", " + user.name : ""}! Here's what's happening today.</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 32 }}>
          {[
            { label: "Total Properties", value: safeProperties.length, icon: "🏠" },
            { label: "Active Properties", value: activeProps, icon: "✅" },
            { label: "Featured", value: featuredProps, icon: "⭐" },
            { label: "Today's Arrivals", value: todayArrivals.length, icon: "✈️" },
          ].map((s, i) => (
            <div key={i} className="admin-card" style={{ textAlign: "center", padding: 24 }}>
              <div style={{ fontSize: "2rem", marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: "2rem", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, color: "#1c1712" }}>{s.value}</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(28,23,18,0.5)", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div className="admin-card">
            <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem", marginBottom: 16 }}>✈️ Today's Arrivals</h3>
            {todayArrivals.length === 0 ? <p style={{ color: "rgba(28,23,18,0.4)", fontSize: "0.88rem" }}>No arrivals today</p> :
              todayArrivals.map((b, i) => (
                <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid rgba(200,149,108,0.1)", display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{b.firstName} {b.lastName}</span>
                  <span style={{ fontSize: "0.82rem", color: "#c8956c" }}>{b.roomId || "Property"}</span>
                </div>
              ))
            }
          </div>
          <div className="admin-card">
            <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem", marginBottom: 16 }}>🧳 Today's Departures</h3>
            {todayDepartures.length === 0 ? <p style={{ color: "rgba(28,23,18,0.4)", fontSize: "0.88rem" }}>No departures today</p> :
              todayDepartures.map((b, i) => (
                <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid rgba(200,149,108,0.1)", display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{b.firstName} {b.lastName}</span>
                  <span style={{ fontSize: "0.82rem", color: "#c8956c" }}>{b.roomId || "Property"}</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    );
  }

  function DestinationsTab() {
    function addDest() {
      const newDest = { ...destForm, id: Date.now() };
      saveDestinations([...destinations, newDest]);
      setDestForm({ id: null, city: "", tag: "", description: "", image: "" });
      setDestEditing(false);
    }
    function removeDest(id) { saveDestinations(destinations.filter(d => d.id !== id)); }
    function startEdit(d) { setDestForm(d); setDestEditing(true); }
    function updateDest() {
      saveDestinations(destinations.map(d => d.id === destForm.id ? destForm : d));
      setDestForm({ id: null, city: "", tag: "", description: "", image: "" });
      setDestEditing(false);
    }
    return (
      <div>
        <div className="admin-section-title">Destinations</div>
        <div className="admin-section-sub">Manage the destination cards shown on the homepage.</div>
        <div className="admin-card">
          <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem", marginBottom: 20 }}>{destEditing ? "Edit Destination" : "Add Destination"}</h3>
          <div className="admin-grid-2">
            <div className="admin-field"><label>City Name</label><input value={destForm.city} onChange={e => setDestForm(f => ({ ...f, city: e.target.value }))} placeholder="Sarandë" /></div>
            <div className="admin-field"><label>Tag</label><input value={destForm.tag} onChange={e => setDestForm(f => ({ ...f, tag: e.target.value }))} placeholder="Coastal" /></div>
          </div>
          <div className="admin-field"><label>Description</label><input value={destForm.description} onChange={e => setDestForm(f => ({ ...f, description: e.target.value }))} placeholder="Crystal clear waters..." /></div>
          <div className="admin-field"><label>Image Path</label><input value={destForm.image} onChange={e => setDestForm(f => ({ ...f, image: e.target.value }))} placeholder="./saranda.jpg" /></div>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="admin-btn admin-btn-gold" onClick={destEditing ? updateDest : addDest}>{destEditing ? "Update" : "Add Destination"}</button>
            {destEditing && <button className="admin-btn" style={{ background: "transparent", border: "1px solid rgba(28,23,18,0.15)", color: "#1c1712" }} onClick={() => { setDestEditing(false); setDestForm({ id: null, city: "", tag: "", description: "", image: "" }); }}>Cancel</button>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {destinations.map(d => (
            <div key={d.id} className="admin-card" style={{ padding: 20 }}>
              {d.image && <img src={d.image} alt={d.city} style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, marginBottom: 12 }} />}
              <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 4 }}>{d.city}</div>
              <div style={{ fontSize: "0.78rem", color: "#c8956c", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>{d.tag}</div>
              <div style={{ fontSize: "0.82rem", color: "rgba(28,23,18,0.5)", marginBottom: 12 }}>{d.description}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="admin-btn admin-btn-sm" onClick={() => startEdit(d)}>Edit</button>
                <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => removeDest(d.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function Beds24Tab() {
    const realBookings = safeBookings.filter(b => b.firstName !== "Block");
    return (
      <div>
        <div className="admin-section-title">Beds24 Live Bookings</div>
        <div className="admin-section-sub">{realBookings.length} active bookings from Beds24</div>
        {realBookings.length === 0 ? (
          <div className="admin-card" style={{ textAlign: "center", padding: 60, color: "rgba(28,23,18,0.4)" }}>No bookings found</div>
        ) : realBookings.map((b, i) => (
          <div key={i} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 4 }}>{b.firstName} {b.lastName}</div>
              <div style={{ fontSize: "0.82rem", color: "rgba(28,23,18,0.5)" }}>{b.firstNight} → {b.lastNight} · {b.numAdult} guest{b.numAdult > 1 ? "s" : ""}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 600, color: "#c8956c", fontSize: "1.1rem" }}>€{b.price || b.totalPrice || "—"}</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(28,23,18,0.4)" }}>{b.roomId || "Room"}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function BlogTab() {
    const safeBlogForm = blogForm;
    async function saveBlog(e) {
      e.preventDefault();
      try {
        const url = blogEditing ? `${API}/blog/${blogEditing}` : `${API}/blog`;
        const method = blogEditing ? "PUT" : "POST";
        const res = await fetch(url, { method, headers, body: JSON.stringify(safeBlogForm) });
        if (!res.ok) throw new Error("Failed to save");
        setMsg("Blog post saved!");
        fetchBlogs();
        setBlogView("list");
        setBlogEditing(null);
        setBlogForm({ title: "", slug: "", excerpt: "", content: "", image: "", category: "Travel", featured: false });
        setTimeout(() => setMsg(""), 3000);
      } catch (e) { setMsg("Error: " + e.message); }
    }
    async function deleteBlog(id) {
      if (!window.confirm("Delete this post?")) return;
      await fetch(`${API}/blog/${id}`, { method: "DELETE", headers });
      fetchBlogs();
    }
    function startEditBlog(b) {
      setBlogForm({ title: b.title || "", slug: b.slug || "", excerpt: b.excerpt || "", content: b.content || "", image: b.image || "", category: b.category || "Travel", featured: b.featured || false });
      setBlogEditing(b._id);
      setBlogView("form");
    }
    const safeBlogs = Array.isArray(blogs) ? blogs : [];
    if (blogView === "form") return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
          <button className="admin-btn admin-btn-sm" onClick={() => { setBlogView("list"); setBlogEditing(null); }}>← Back</button>
          <div className="admin-section-title">{blogEditing ? "Edit Post" : "New Blog Post"}</div>
        </div>
        {msg && <div className="admin-success">{msg}</div>}
        <form onSubmit={saveBlog}>
          <div className="admin-card">
            <div className="admin-grid-2">
              <div className="admin-field"><label>Title</label><input value={safeBlogForm.title} onChange={e => setBlogForm(f => ({ ...f, title: e.target.value }))} placeholder="10 Hidden Gems in Albania" required /></div>
              <div className="admin-field"><label>Slug</label><input value={safeBlogForm.slug} onChange={e => setBlogForm(f => ({ ...f, slug: e.target.value }))} placeholder="hidden-gems-albania" required /></div>
            </div>
            <div className="admin-grid-2">
              <div className="admin-field"><label>Category</label><select value={safeBlogForm.category} onChange={e => setBlogForm(f => ({ ...f, category: e.target.value }))}><option>Travel</option><option>Tips</option><option>Destinations</option><option>Lifestyle</option></select></div>
              <div className="admin-field"><label>Cover Image Path</label><input value={safeBlogForm.image} onChange={e => setBlogForm(f => ({ ...f, image: e.target.value }))} placeholder="/uploads/blog-cover.jpg" /></div>
            </div>
            <div className="admin-field"><label>Excerpt</label><textarea value={safeBlogForm.excerpt} onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Short description..." style={{ minHeight: 80 }} /></div>
            <div className="admin-field"><label>Content</label><textarea value={safeBlogForm.content} onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))} placeholder="Full blog post content..." style={{ minHeight: 240 }} /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1c1712" }}>
              <input type="checkbox" checked={safeBlogForm.featured} onChange={e => setBlogForm(f => ({ ...f, featured: e.target.checked }))} />Featured post
            </label>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button type="submit" className="admin-btn admin-btn-gold">{blogEditing ? "Save Changes" : "Publish Post"}</button>
            <button type="button" className="admin-btn" style={{ background: "transparent", border: "1px solid rgba(28,23,18,0.15)", color: "#1c1712" }} onClick={() => setBlogView("list")}>Cancel</button>
          </div>
        </form>
      </div>
    );
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div><div className="admin-section-title">Blog Posts</div><div className="admin-section-sub">{safeBlogs.length} posts published</div></div>
          <button className="admin-btn admin-btn-gold" onClick={() => setBlogView("form")}>+ New Post</button>
        </div>
        {msg && <div className="admin-success" style={{ marginBottom: 16 }}>{msg}</div>}
        {safeBlogs.map(b => (
          <div key={b._id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {b.image && <img src={b.image} alt={b.title} style={{ width: 72, height: 56, objectFit: "cover", borderRadius: 8 }} />}
              <div>
                <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 2 }}>{b.title}</div>
                <div style={{ fontSize: "0.82rem", color: "rgba(28,23,18,0.5)" }}>{b.category} · {b.slug}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="admin-btn admin-btn-sm" onClick={() => startEditBlog(b)}>Edit</button>
              <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => deleteBlog(b._id)}>Delete</button>
            </div>
          </div>
        ))}
        {safeBlogs.length === 0 && <div className="admin-card" style={{ textAlign: "center", padding: 60, color: "rgba(28,23,18,0.4)" }}>No blog posts yet.</div>}
      </div>
    );
  }

  return (
    <div className="admin-dash">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <img src="/bookalblogo.png" alt="BookingAlb" />
        </div>
        <nav className="admin-nav">
          {NAV.map(item => (
            <button key={item.id} className={`admin-nav-item${tab === item.id ? " active" : ""}`} onClick={() => setTab(item.id)}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-user-name">{user?.name || "Admin"}</div>
          <button className="admin-logout-btn" onClick={logout}>Log Out</button>
        </div>
      </aside>
      <main className="admin-main">
        {tab === "dashboard" && <DashboardTab />}
        {tab === "properties" && <AdminProperties />}
        {tab === "cars" && <AdminCars />}
        {tab === "destinations" && <DestinationsTab />}
        {tab === "beds24" && <Beds24Tab />}
        {tab === "blog" && <BlogTab />}
      </main>
    </div>
  );
}